/**
 * QC (08 + 00c) — server response shapes (mirror of the `qc` module, Biologiste surface).
 *
 * The server returns the standard { success, data } envelope; the api/useApiQuery layer unwraps `data`,
 * so these types describe the UNWRAPPED payload. BigInt ids serialize to JSON numbers/strings, so every
 * id is typed `string | number` and normalized to a string with `idStr()` before use as a key/param.
 *
 * Only fields the screens read are typed; the server may carry more keys (forward-compatible). Where a
 * contract is uncertain we keep a field optional and degrade gracefully rather than invent it. Every
 * path + capability is code-verified against server/src/modules/qc/{routes,controller,validators,
 * *.service}.js.
 */

/** A normalized id — the wire carries either; callers compare/scope on the string form. */
export type Id = string | number;

/** Coerce a wire id to a stable string (BigInt-safe; never produces "[object Object]"). */
export function idStr(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}

/** The three QC material levels (validator enum). */
export type Niveau = "niveau_1" | "niveau_2" | "niveau_3";
export const NIVEAUX: ReadonlyArray<Niveau> = ["niveau_1", "niveau_2", "niveau_3"];

// ── LotControle lifecycle ──────────────────────────────────────────────────────────

/** brouillon → valide → expire (a re-cal is a NEW lot, never an in-place stat edit). */
export type LotStatut = "brouillon" | "valide" | "expire" | string;

/** A QC lot row (GET /qc/lots). */
export interface LotControle {
  id: Id;
  code: string;
  fabricant?: string | null;
  niveau: Niveau;
  parametre_code: string;
  automate_id?: Id | null;
  cible?: number | null;
  ecart_type?: number | null;
  unite?: string | null;
  date_peremption: string;
  statut: LotStatut;
}

/** Result of POST /qc/lots (enrol). */
export interface LotEnrolled {
  lot_id: Id;
  statut: "brouillon" | string;
}

/** Result of POST /qc/lots/:id/valider (freeze stats). */
export interface LotValidated {
  lot_id: Id;
  statut: "valide" | string;
  cible: number;
  ecart_type: number;
}

/** Result of POST /qc/lots/sweep (idempotent expiry sweep). */
export interface SweepResult {
  swept: Id[];
}

// ── RetenueQc (the release hold) ─────────────────────────────────────────────────────

export type RetenueStatut = "ouverte" | "levee" | string;

/** An open hold row (GET /qc/retenues). */
export interface RetenueQc {
  id: Id;
  parametre_code: string;
  automate_id?: Id | null;
  fenetre_debut: string;
  fenetre_fin: string;
  statut: RetenueStatut;
  motif?: string | null;
}

/** Result of POST /qc/retenues (open a manual hold). */
export interface HoldOpened {
  retenue_id: Id;
  statut: RetenueStatut;
}

/** Result of POST /qc/retenues/:id/lever. */
export interface HoldLifted {
  retenue_id: Id;
  statut: "levee" | string;
}

/** Result of GET /qc/retenues/est-retenu (pure containment probe). */
export interface EstRetenu {
  retenu: boolean;
}

// ── Observation entry → auto-evaluation (Westgard) ───────────────────────────────────

export type Verdict = "accepte" | "rejete" | "alerte" | "en_cours" | string;

/** Happy path of POST /qc/observations. */
export interface ObservationOk {
  point_id: Id;
  run_id: Id;
  verdict: Verdict;
  regles_violees: string[];
  z: number;
  retenue_id?: Id | null;
}

/** Degraded path of POST /qc/observations (no valide lot / ecart_type ≤ 0). */
export interface ObservationDegraded {
  degraded: true;
  reason: "no_valide_lot" | "ecart_type_invalide" | string;
}

export type ObservationResult = ObservationOk | ObservationDegraded;

export function isDegraded(r: ObservationResult): r is ObservationDegraded {
  return (r as ObservationDegraded).degraded === true;
}

// ── Levey-Jennings chart series ──────────────────────────────────────────────────────

/** The ±1/2/3 SD bands from the lot's FROZEN stats (null until a series exists). */
export interface LjBands {
  cible: number;
  plus1: number;
  minus1: number;
  plus2: number;
  minus2: number;
  plus3: number;
  minus3: number;
}

/** One charted point (GET /qc/points → points[]). */
export interface LjPoint {
  id: string;
  at: string;
  valeur: number;
  z: number;
  verdict: Verdict;
  regles_violees: string[];
}

/** GET /qc/points payload (the Levey-Jennings series). */
export interface ChartSeries {
  bands: LjBands | null;
  points: LjPoint[];
}
